import type {
  CompactEvent,
  CompactEventCode,
  EventAction,
  KnittingEvent,
  Session,
} from '../types/session';
import type { z } from 'zod';
import type { SessionWireSchema } from '../types/schemas';
import { CURRENT_SESSION_VERSION } from '../types/state';

export type SessionWire = z.infer<typeof SessionWireSchema>;

const ACTION_TO_CODE: Record<EventAction, CompactEventCode> = {
  START: 'S',
  PAUSE: 'P',
  RESUME: 'R',
  NEXT_ROW: 'N',
  PREV_ROW: 'B',
  EXPAND_NODE: 'X',
};

const CODE_TO_ACTION: Record<CompactEventCode, EventAction> = {
  S: 'START',
  P: 'PAUSE',
  R: 'RESUME',
  N: 'NEXT_ROW',
  B: 'PREV_ROW',
  X: 'EXPAND_NODE',
};

export function encodeEvent(e: KnittingEvent, t0Ms: number): CompactEvent {
  const ts = Date.parse(e.timestamp);
  const delta = Math.max(0, ts - t0Ms);
  const arg = e.action === 'EXPAND_NODE' ? (e.nodeId ?? null) : null;
  return [ACTION_TO_CODE[e.action], delta, arg];
}

export function decodeEvent(c: CompactEvent, t0Ms: number): KnittingEvent {
  const [code, delta, arg] = c;
  const action = CODE_TO_ACTION[code];
  const event: KnittingEvent = {
    action,
    timestamp: new Date(t0Ms + delta).toISOString(),
  };
  if (action === 'EXPAND_NODE' && arg) {
    event.nodeId = arg;
  }
  return event;
}

export function encodeSession(s: Session): SessionWire {
  const t0 = Date.parse(s.startedAt);
  return {
    version: CURRENT_SESSION_VERSION,
    id: s.id,
    patternId: s.patternId,
    title: s.title,
    status: s.status,
    startedAt: s.startedAt,
    updatedAt: s.updatedAt,
    snapshot: s.snapshot,
    history: s.history.map((e) => encodeEvent(e, t0)),
    ...(s.rowTimings ? { rowTimings: s.rowTimings } : {}),
    ...(s.totalTimeMs !== undefined ? { totalTimeMs: s.totalTimeMs } : {}),
    ...(s.paramValues ? { paramValues: s.paramValues } : {}),
    ...(s.compiledDsl ? { compiledDsl: s.compiledDsl } : {}),
    ...(s.metadata ? { metadata: s.metadata } : {}),
  };
}

export function decodeSession(w: SessionWire): Session {
  const t0 = Date.parse(w.startedAt);
  return {
    id: w.id,
    patternId: w.patternId,
    title: w.title,
    status: w.status,
    startedAt: w.startedAt,
    updatedAt: w.updatedAt,
    snapshot: w.snapshot,
    history: w.history.map((c) => decodeEvent(c, t0)),
    ...(w.rowTimings ? { rowTimings: w.rowTimings } : {}),
    ...(w.totalTimeMs !== undefined ? { totalTimeMs: w.totalTimeMs } : {}),
    ...(w.paramValues ? { paramValues: w.paramValues } : {}),
    ...(w.compiledDsl ? { compiledDsl: w.compiledDsl } : {}),
    ...(w.metadata ? { metadata: w.metadata } : {}),
  };
}
